// QuestionEditor.jsx
import React, { forwardRef, useState } from "react";
import PropTypes from 'prop-types';
import { Plus, Trash2, MinusCircle } from "lucide-react";
import ImageUploadComponent from "../ImageUploadComponent/ImageUploadComponent";
import DeleteConfirmationDialog from "../../../../components/dialogs/DeleteConfirmationDialog";
// import './QuestionEditor.css'; // Assuming your styles are in QuestionForm.css or TrainingCreation.css

const QuestionEditor = forwardRef(({
  question,
  index,
  onQuestionChange,
  onDeleteQuestion,
  onAddAnswer,
  onRemoveAnswer,
  imageData,
  disableDelete = false,
  errors = {}
}, ref) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    questionImagePreviews,
    explanationImagePreviews,
    onQuestionImageChange,
    onExplanationImageChange,
    onRemoveQuestionImage,
    onRemoveExplanationImage,
  } = imageData;

  // --- START DEBUG LOGS (can be kept for now) ---
  console.log(`[QuestionEditor index ${index}] Rendering. Received errors:`, JSON.parse(JSON.stringify(errors)));
  if (errors && errors.question) {
    console.log(`[QuestionEditor index ${index}] errors.question value: "${errors.question}"`);
  }
  const hasQuestionError = !!(errors && errors.question && String(errors.question).trim() !== "");
  // --- END DEBUG LOGS ---

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    onDeleteQuestion(index);
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <div
        ref={ref}
        className={`training-question-form ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} // Assuming this class maps to .training-question-detail or similar
        id={`question-editor-${index}`}
      >
        <div
          className="training-question-header"
        // Remove inline style if you want CSS file to control it
        // style={{ display: 'flex', alignItems: 'stretch' }}
        >
          <div
            className="training-question-number"
          // Remove inline style if you want CSS file to control it
          // style={{ alignSelf: 'center' }}
          >
            {index + 1}.
          </div>

          {/* --- START: NEW WRAPPER for input and its error --- */}
          <div className="training-question-input-wrapper"> {/* This class is crucial for CSS targeting */}
            <input
              type="text"
              id={`question-text-input-${index}`}
              className="training-question-input"
              placeholder="שאלה"
              value={question.question || ""}
              onChange={(e) => onQuestionChange(index, "question", e.target.value)}
              aria-invalid={hasQuestionError}
              aria-describedby={hasQuestionError ? `question-error-msg-${index}` : undefined}
            />
            {/* Error message for THIS input, INSIDE the wrapper, AFTER the input */}
            {hasQuestionError && (
              <p id={`question-error-msg-${index}`} className="error-text" role="alert">
                {errors.question}
              </p>
            )}
          </div>
          {/* --- END: NEW WRAPPER --- */}

          {!disableDelete && (
            <button
              onClick={handleDeleteClick}
              className="training-button training-button-danger" // Ensure these classes match your CSS
              type="button"
              title={disableDelete ? "לא ניתן למחוק את השאלה האחרונה" : "מחק שאלה"}
            >
              <Trash2 size={16} />
              מחק שאלה
            </button>
          )}
        </div>
      
      {onQuestionImageChange && (
        <ImageUploadComponent
          imageUrl={questionImagePreviews && questionImagePreviews[index]}
          onImageUpload={(e) => onQuestionImageChange(index, e)}
          onImageRemove={() => onRemoveQuestionImage && onRemoveQuestionImage(index)}
          label="תמונה לשאלה (אופציונלי)"
          placeholderText="העלאת תמונה לשאלה"
          inputId={`question-image-upload-${index}`}
        />
      )}
      
      <div className="training-question-options" id={`question-${index}-answers-section`}>
        {(question.answers || []).map((answer, answerIndex) => (
          <div key={`answer-${index}-${answerIndex}`} className="training-question-option">
            <input
              type="radio"
              id={`question-${index}-answer-radio-${answerIndex}`}
              name={`question-${index}-correct-answer`}
              checked={question.correct === answerIndex}
              onChange={() => onQuestionChange(index, "correct", answerIndex)}
              className="training-question-radio"
            />
            <input
              type="text"
              id={`question-${index}-answer-input-${answerIndex}`}
              className="training-answer-input"
              placeholder={`תשובה ${answerIndex + 1}`}
              value={answer || ""}
              onChange={(e) => onQuestionChange(index, "answers", {index: answerIndex, text: e.target.value})}
            />
            {(question.answers || []).length > 2 && (
              <button
                onClick={() => onRemoveAnswer(index, answerIndex)}
                className="training-button training-button-danger training-button-small"
                title="הסר תשובה"
                type="button"
              >
                <MinusCircle size={16} />
              </button>
            )}
          </div>
        ))}
        {/* Inline style for error text here should ideally be moved to CSS file */}
        {errors.answersGeneral && (
          <p 
            id={`answers-error-msg-${index}`} 
            className="error-text answers-general-error" // Add answers-general-error for specific styling
            role="alert" 
            // style={{width: '100%', textAlign: 'right'}} // Move to CSS
          >
            {errors.answersGeneral}
          </p>
        )}
        
        <button
          onClick={() => onAddAnswer(index)}
          className="training-button training-button-secondary training-button-small add-answer-btn"
          style={{ marginTop: "8px" }}
          type="button"
        >
          <Plus size={16} />
          הוסף תשובה
        </button>
      </div>
      
      <div className="training-explanation-section">
        <label htmlFor={`explanation-input-${index}`} className="training-explanation-label">
          הסבר:
        </label>
        <textarea
          id={`explanation-input-${index}`}
          className="training-explanation-input"
          value={question.explanation || ""}
          onChange={(e) => onQuestionChange(index, "explanation", e.target.value)}
          placeholder="הכנס הסבר"
          rows={3}
        />
      </div>
      
      {onExplanationImageChange && (
        <ImageUploadComponent
          imageUrl={explanationImagePreviews && explanationImagePreviews[index]}
          onImageUpload={(e) => onExplanationImageChange(index, e)}
          onImageRemove={() => onRemoveExplanationImage && onRemoveExplanationImage(index)}
          label="תמונה להסבר (אופציונלי)"
          placeholderText="העלאת תמונה להסבר"
          inputId={`explanation-image-upload-${index}`}
        />
      )}
      </div>
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="האם אתה בטוח שברצונך למחוק את השאלה?"
      />
    </>
  );
});

QuestionEditor.displayName = 'QuestionEditor';

QuestionEditor.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string,
    answers: PropTypes.arrayOf(PropTypes.string),
    correct: PropTypes.number,
    explanation: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  onDeleteQuestion: PropTypes.func.isRequired,
  onAddAnswer: PropTypes.func.isRequired,
  onRemoveAnswer: PropTypes.func.isRequired,
  imageData: PropTypes.shape({
    questionImagePreviews: PropTypes.object,
    explanationImagePreviews: PropTypes.object,
    onQuestionImageChange: PropTypes.func,
    onExplanationImageChange: PropTypes.func,
    onRemoveQuestionImage: PropTypes.func,
    onRemoveExplanationImage: PropTypes.func,
  }).isRequired,
  disableDelete: PropTypes.bool,
  errors: PropTypes.shape({
    question: PropTypes.string,
    answersGeneral: PropTypes.string,
  }),
};

export default QuestionEditor;